// FILE: src/components/app/bases/TransactionsBaseComponent/TransactionsTableSettingsModal/TransactionsTableSettingsModal.js
import React, { useEffect, useMemo, useState } from "react";

/**
 * Props (matches TransactionsBaseComponent usage):
 * - open: boolean
 * - onClose(): void
 * - allColumns: string[]
 * - selectedColumns: string[]
 * - viewOptions: { density: "comfortable"|"cozy"|"compact", striped: boolean, stickyFirst: boolean }
 * - onApply({ columns: string[], options: { density, striped, stickyFirst } }): void
 */
export default function TransactionsTableSettingsModal({
  open,
  onClose,
  allColumns = [],
  selectedColumns = [],
  viewOptions = { density: "compact", striped: true, stickyFirst: true },
  onApply,
}) {
  /* ---------- normalize inputs ---------- */
  const ALL_COLS = useMemo(() => normalizeColumns(allColumns), [allColumns]);
  const allKeys = useMemo(() => ALL_COLS.map((c) => c.key), [ALL_COLS]);

  const DEFAULT_SELECTED = useMemo(() => {
    // sensible default if empty; otherwise honor selectedColumns input
    const fallback = pickExisting(["date", "typeName", "amount", "balance", "description", "tagPath", "accountName"], ALL_COLS);
    const incoming = pickExisting(selectedColumns, ALL_COLS);
    return incoming.length ? incoming : (fallback.length ? fallback : allKeys);
  }, [selectedColumns, ALL_COLS, allKeys]);

  const DEFAULTS = useMemo(
    () => ({
      selectedColumns: DEFAULT_SELECTED,
      density: validDensity(viewOptions?.density) ? viewOptions.density : "compact",
      striped: typeof viewOptions?.striped === "boolean" ? viewOptions.striped : true,
      stickyFirst: typeof viewOptions?.stickyFirst === "boolean" ? viewOptions.stickyFirst : true,
      wrapDescription: !!viewOptions?.wrapDescription,
      showTagsAsChips: viewOptions?.showTagsAsChips !== false, // default true
    }),
    [DEFAULT_SELECTED, viewOptions]
  );

  /* ---------- state ---------- */
  const [selected, setSelected] = useState(DEFAULTS.selectedColumns);
  const [density, setDensity] = useState(DEFAULTS.density);
  const [striped, setStriped] = useState(DEFAULTS.striped);
  const [stickyFirst, setStickyFirst] = useState(DEFAULTS.stickyFirst);
  const [wrapDescription, setWrapDescription] = useState(DEFAULTS.wrapDescription);
  const [showTagsAsChips, setShowTagsAsChips] = useState(DEFAULTS.showTagsAsChips);

  const [query, setQuery] = useState("");

  /* ---------- init/reset on open ---------- */
  useEffect(() => {
    if (!open) return;
    setSelected(DEFAULTS.selectedColumns);
    setDensity(DEFAULTS.density);
    setStriped(DEFAULTS.striped);
    setStickyFirst(DEFAULTS.stickyFirst);
    setWrapDescription(DEFAULTS.wrapDescription);
    setShowTagsAsChips(DEFAULTS.showTagsAsChips);
    setQuery("");

    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, DEFAULTS.selectedColumns, DEFAULTS.density, DEFAULTS.striped, DEFAULTS.stickyFirst, DEFAULTS.wrapDescription, DEFAULTS.showTagsAsChips]);

  /* ---------- derived lists (hooks before any return) ---------- */
  const available = useMemo(() => allKeys.filter((k) => !selected.includes(k)), [allKeys, selected]);

  const filteredSelected = useMemo(
    () => (query ? selected.filter((k) => includesI(labelFor(k, ALL_COLS), query) || includesI(k, query)) : selected),
    [selected, query, ALL_COLS]
  );

  const filteredAvailable = useMemo(
    () =>
      query
        ? available.filter((k) => includesI(labelFor(k, ALL_COLS), query) || includesI(k, query))
        : available,
    [available, query, ALL_COLS]
  );

  /* ---------- early out after hooks ---------- */
  if (!open) return null;

  /* ---------- actions ---------- */
  function addColumn(key) {
    if (!allKeys.includes(key)) return;
    setSelected((cur) => (cur.includes(key) ? cur : [...cur, key]));
  }
  function removeColumn(key) { setSelected((cur) => cur.filter((k) => k !== key)); }
  function moveUp(key) {
    setSelected((cur) => {
      const i = cur.indexOf(key);
      if (i <= 0) return cur;
      const next = cur.slice();
      [next[i - 1], next[i]] = [next[i], next[i - 1]];
      return next;
    });
  }
  function moveDown(key) {
    setSelected((cur) => {
      const i = cur.indexOf(key);
      if (i === -1 || i >= cur.length - 1) return cur;
      const next = cur.slice();
      [next[i + 1], next[i]] = [next[i], next[i + 1]];
      return next;
    });
  }
  function clearSelected() { setSelected([]); }
  function selectAll() { setSelected(allKeys.slice()); }
  function applyPreset(name) {
    const n = String(name || "").toLowerCase();
    if (n === "all") return selectAll();
    if (n === "standard") return setSelected(DEFAULT_SELECTED);
    const minimal = pickExisting(["date", "description"], ALL_COLS);
    setSelected(minimal.length ? minimal : DEFAULT_SELECTED.slice(0, 2));
  }
  function handleApply() {
    const payload = {
      columns: selected,
      options: { density, striped, stickyFirst, wrapDescription, showTagsAsChips },
    };
    onApply?.(payload);
  }
  function handleReset() {
    setSelected(DEFAULTS.selectedColumns);
    setDensity(DEFAULTS.density);
    setStriped(DEFAULTS.striped);
    setStickyFirst(DEFAULTS.stickyFirst);
    setWrapDescription(DEFAULTS.wrapDescription);
    setShowTagsAsChips(DEFAULTS.showTagsAsChips);
  }

  /* ---------- render ---------- */
  return (
    <div
      className="modal d-block"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tx-settings-title"
      style={{ background: "rgba(0,0,0,0.35)", zIndex: 1050 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" style={{ maxWidth: 820 }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h5 id="tx-settings-title" className="modal-title">Table settings</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div className="btn-group" role="group" aria-label="Presets">
                <button className="btn btn-sm btn-outline-secondary" onClick={() => applyPreset("Minimal")}>Minimal</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => applyPreset("Standard")}>Standard</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => applyPreset("All")}>All</button>
              </div>

              <div>
                <input
                  className="form-control form-control-sm"
                  placeholder="Find a column…"
                  aria-label="Filter columns"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-12 col-md-6">
                <div className="small fw-semibold text-secondary text-uppercase mb-2">Selected columns</div>
                <div className="border rounded p-2">
                  {filteredSelected.length === 0 && (
                    <div className="text-secondary small px-2 py-1">No columns selected</div>
                  )}
                  {filteredSelected.map((key, idx) => (
                    <div
                      key={key}
                      className="d-flex align-items-center justify-content-between gap-2 py-1 px-2"
                      title={key}
                      style={{
                        borderBottom:
                          idx < filteredSelected.length - 1
                            ? "1px dashed var(--bs-border-color, #dee2e6)"
                            : "none",
                      }}
                    >
                      <div className="text-truncate">{labelFor(key, ALL_COLS)}</div>
                      <div className="d-flex align-items-center gap-1">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          title={`Move ${key} up`}
                          aria-label={`Move ${key} up`}
                          onClick={() => moveUp(key)}
                          disabled={selected.indexOf(key) === 0}
                        >
                          ↑
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          title={`Move ${key} down`}
                          aria-label={`Move ${key} down`}
                          onClick={() => moveDown(key)}
                          disabled={selected.indexOf(key) === selected.length - 1}
                        >
                          ↓
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          title={`Remove ${key}`}
                          aria-label={`Remove ${key}`}
                          onClick={() => removeColumn(key)}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="d-flex gap-2 mt-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={clearSelected}>Clear</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={selectAll}>Select all</button>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="small fw-semibold text-secondary text-uppercase mb-2">Available columns</div>
                <div className="border rounded p-2">
                  {filteredAvailable.length === 0 && (
                    <div className="text-secondary small px-2 py-1">No matches</div>
                  )}
                  {filteredAvailable.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="w-100 d-flex align-items-center justify-content-between gap-2 py-1 px-2 rounded text-start btn btn-sm btn-light mb-1"
                      title={`Add ${key}`}
                      aria-label={`Add ${key}`}
                      onClick={() => addColumn(key)}
                    >
                      <div className="text-truncate">{labelFor(key, ALL_COLS)}</div>
                      <span className="opacity-50">＋</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 small fw-semibold text-secondary text-uppercase">Layout</div>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label small fw-semibold">Density</label>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className={`btn btn-sm ${density === "comfortable" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setDensity("comfortable")}
                  >
                    Comfortable
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${density === "cozy" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setDensity("cozy")}
                  >
                    Cozy
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${density === "compact" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setDensity("compact")}
                  >
                    Compact
                  </button>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label small fw-semibold">Row styling</label>
                <div className="d-flex flex-wrap gap-3">
                  <label className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={striped}
                      onChange={(e) => setStriped(e.target.checked)}
                    />
                    <span className="form-check-label">Striped rows</span>
                  </label>
                  <label className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={stickyFirst}
                      onChange={(e) => setStickyFirst(e.target.checked)}
                    />
                    <span className="form-check-label">Sticky first column</span>
                  </label>
                  <label className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={wrapDescription}
                      onChange={(e) => setWrapDescription(e.target.checked)}
                    />
                    <span className="form-check-label">Wrap descriptions</span>
                  </label>
                  <label className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={showTagsAsChips}
                      onChange={(e) => setShowTagsAsChips(e.target.checked)}
                    />
                    <span className="form-check-label">Show tags as chips</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-3 small text-secondary">Changes apply to this user and persist across sessions.</div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={handleReset}>Reset</button>
            <button className="btn btn-primary" onClick={handleApply}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function normalizeColumns(list) {
  return (list || []).map((it) =>
    typeof it === "string" ? { key: it, label: it } : { key: it.key, label: it.label || it.key }
  );
}
function pickExisting(arr, all) {
  const allowed = new Set(all.map((c) => c.key));
  return (arr || []).filter((k) => allowed.has(k));
}
function labelFor(key, all) {
  return all.find((c) => c.key === key)?.label || key;
}
function includesI(hay, needle) {
  return String(hay).toLowerCase().includes(String(needle).toLowerCase());
}
function validDensity(v) {
  return v === "comfortable" || v === "cozy" || v === "compact";
}
